'use client';

import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { MoyasarCheckoutModal } from '@kit/moyasar/components';
import { toMinorUnits } from '@kit/shared/currency';
import { useTenantCurrency } from '@kit/ui/currency-icon';
import { toast } from '@kit/ui/sonner';

import { useBookingPresentation } from './booking-presentation';

interface MoyasarCheckoutDialogProps {
  isOpen: boolean;
  onClose: () => void;
  checkoutData: {
    sessionId: string;
    provider: string;
    bookingId?: string;
    participantId?: string;
    isClassPayment?: boolean;
    isPackagePayment?: boolean;
    userPackageId?: string;
    purchaseId?: string;
    publishableKey?: string;
    userId?: string;
    playerCount?: number;
    tenantId?: string;
    tenantName?: string;
    locationId?: string;
    isAddonPayment?: boolean;
    isGuestPassPayment?: boolean;
    guestPassId?: string;
    isMembershipPayment?: boolean;
    userMembershipId?: string;
    // Self-serve atomic membership PURCHASE (distinct from isMembershipPayment,
    // which settles an existing membership). No row exists yet — the membership
    // is materialized on payment confirm from metadata. purchaseId binds the
    // payment; planId/period are echoed for the confirm path.
    isMembershipPurchase?: boolean;
    membershipPurchaseId?: string;
    membershipPlanId?: string;
    membershipPeriod?: string;
    packageData?: {
      id: string;
      name: string;
      price: number;
      booking_count: number;
      validity_months: number;
      validity_days?: number | null;
    };
    // Optional extra metadata merged into whichever branch the widget picks.
    // Used for fixed-slot package purchases to carry the selection
    // (days, time, trainer) through Moyasar to the webhook fulfiller.
    extraMetadata?: Record<string, string>;
  } | null;
  bookingDetails: {
    amount: number;
    description: string;
  } | null;
  returnUrl?: string;
  /**
   * Optional alternative payment control (e.g. a native Apple Pay button)
   * rendered above the embedded card form. Opt-in per flow — flows that don't
   * pass it are unchanged.
   */
  applePaySlot?: React.ReactNode;
  onSuccess?: () => void;
}

export function MoyasarCheckoutDialog({
  isOpen,
  onClose,
  checkoutData,
  bookingDetails,
  returnUrl,
  applePaySlot,
  onSuccess,
}: MoyasarCheckoutDialogProps) {
  const presentation = useBookingPresentation();
  const [isProcessing, setIsProcessing] = useState(false);
  const tenantCurrency = useTenantCurrency();
  const { t } = useTranslation();

  // Moyasar is a Saudi PSP — settles in SAR only. Forced to SAR regardless
  // of tenant's display currency. PSP currency gating belongs at the tenant
  // currency-picker level (`apps/web/app/api/currencies/route.ts`); see
  // review item #5/#6.
  if (tenantCurrency !== 'SAR' && bookingDetails) {
    console.warn(
      `[Moyasar] Tenant displays ${tenantCurrency} but Moyasar will charge SAR — amounts diverge (no FX conversion applied)`,
    );
  }

  // Handle payment success - save payment ID before 3DS redirect
  const handlePaymentSuccess = async (paymentId: string) => {
    setIsProcessing(true);

    try {
      // Save the payment ID to backend BEFORE 3DS redirect
      // This is critical per Moyasar docs - in case connection drops during 3DS
      console.log('Saving Moyasar payment ID before 3DS redirect:', paymentId);

      // Check if this is a court booking, class payment, or package purchase
      if (checkoutData?.isPackagePayment && checkoutData?.userPackageId) {
        // Package payment (assigned package)
        const { saveMoyasarPackagePaymentBeforeRedirectAction } =
          await import('../../payment/_lib/server/package-payment.actions');

        const result = await saveMoyasarPackagePaymentBeforeRedirectAction({
          paymentId,
          packageId: checkoutData.userPackageId,
          packagePaymentId: checkoutData.extraMetadata?.package_payment_id,
        });

        if (!result.success) {
          console.error(
            'Failed to save package payment ID before redirect:',
            result.error,
          );
        } else {
          console.log(
            'Successfully saved package payment ID before 3DS redirect',
          );
        }
      } else if (
        checkoutData?.isMembershipPayment &&
        checkoutData?.userMembershipId
      ) {
        // Membership payment (outstanding dues on user_memberships)
        const { saveMembershipPaymentBeforeRedirectAction } =
          await import('../../payment/_lib/server/membership-payment.actions');

        const result = await saveMembershipPaymentBeforeRedirectAction({
          paymentId,
          membershipId: checkoutData.userMembershipId,
          provider: 'moyasar',
        });

        if (!result.success) {
          console.error(
            'Failed to save membership payment ID before redirect:',
            result.error,
          );
        } else {
          console.log(
            'Successfully saved membership payment ID before 3DS redirect',
          );
        }
      } else if (
        checkoutData?.isGuestPassPayment &&
        checkoutData?.guestPassId
      ) {
        // Guest pass payment (inviter pays inline for a guest's visit pass)
        const { saveMoyasarGuestPassPaymentBeforeRedirectAction } =
          await import('../../payment/guest/_lib/server/guest-pass-payment.actions');

        const result = await saveMoyasarGuestPassPaymentBeforeRedirectAction({
          paymentId,
          passId: checkoutData.guestPassId,
        });

        if (!result.success) {
          console.error(
            'Failed to save guest pass payment ID before redirect:',
            result.error,
          );
        } else {
          console.log(
            'Successfully saved guest pass payment ID before 3DS redirect',
          );
        }
      } else if (checkoutData?.isClassPayment && checkoutData?.participantId) {
        // Class participant payment
        const { saveMoyasarClassPaymentBeforeRedirectAction } =
          await import('../../payment/_lib/server/class-payment.actions');

        const result = await saveMoyasarClassPaymentBeforeRedirectAction({
          paymentId,
          participantId: checkoutData.participantId,
        });

        if (!result.success) {
          console.error(
            'Failed to save class payment ID before redirect:',
            result.error,
          );
          // Continue anyway - we'll verify on return
        } else {
          console.log(
            'Successfully saved class payment ID before 3DS redirect',
          );
        }
      } else if (checkoutData?.bookingId) {
        // Court booking payment
        const { saveMoyasarPaymentBeforeRedirectAction } =
          await import('../_lib/server/court-booking/check-moyasar-payment');

        const result = await saveMoyasarPaymentBeforeRedirectAction({
          paymentId,
          bookingId: checkoutData.bookingId,
        });

        if (!result.success) {
          console.error(
            'Failed to save payment ID before redirect:',
            result.error,
          );
          // Continue anyway - we'll verify on return
        } else {
          console.log('Successfully saved payment ID before 3DS redirect');
        }
      } else if (checkoutData?.purchaseId && checkoutData?.packageData) {
        // Package purchase payment
        const { saveMoyasarPackagePaymentBeforeRedirectAction } =
          await import('../_lib/server/court-booking/check-moyasar-payment');

        const result = await saveMoyasarPackagePaymentBeforeRedirectAction({
          paymentId,
          purchaseId: checkoutData.purchaseId,
          packageId: checkoutData.packageData.id,
          userId: checkoutData.userId,
          amount: checkoutData.packageData.price,
          playerCount: checkoutData.playerCount,
        });

        if (!result.success) {
          console.error(
            'Failed to save package payment ID before redirect:',
            result.error,
          );
          // Continue anyway - we'll verify on return
        } else {
          console.log(
            'Successfully saved package payment ID before 3DS redirect',
          );
        }
      }

      // Note: We don't redirect here manually - Moyasar will automatically redirect
      // to callback_url after 3DS completes with ?id=<payment_id> appended

      if (onSuccess) {
        onSuccess();
      }

      // The actual redirect happens automatically by Moyasar after 3DS
    } catch (error) {
      console.error('Error handling payment success:', error);
      // Don't show error to user - payment is already created
      // We'll verify status on the return page
      setIsProcessing(false);
    }
  };

  // Handle payment error
  const handlePaymentError = (error: Error) => {
    console.error('Payment error:', error);
    toast.error(
      t('common:memberClub.payment.toastPaymentRetry', {
        defaultValue: 'Payment failed. Please try again.',
      }),
    );
    setIsProcessing(false);
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  if (
    !checkoutData ||
    checkoutData.provider !== 'moyasar' ||
    !bookingDetails ||
    !checkoutData.publishableKey
  ) {
    return null;
  }

  // Use current window origin to maintain tenant domain (multi-tenant support)
  // The user is already on the correct tenant domain, so we use that for the callback
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';

  // Moyasar will append ?id=<payment_id> to this URL after 3DS redirect
  // Use custom return URL if provided, otherwise default to booking/package/class return page
  const callbackUrl =
    returnUrl ||
    (checkoutData.extraMetadata?.type === 'product_sale' &&
    checkoutData.extraMetadata?.order_id
      ? `${siteUrl}/payment/order/${checkoutData.extraMetadata.order_id}`
      : checkoutData.isPackagePayment && checkoutData.userPackageId
        ? `${siteUrl}/payment/package/${checkoutData.userPackageId}`
        : checkoutData.isMembershipPayment && checkoutData.userMembershipId
          ? `${siteUrl}/payment/membership/${checkoutData.userMembershipId}`
          : checkoutData.isMembershipPurchase
            ? `${siteUrl}/memberships?membership_purchase=1`
            : checkoutData.isGuestPassPayment && checkoutData.guestPassId
              ? `${siteUrl}/payment/guest/${checkoutData.guestPassId}`
              : checkoutData.purchaseId
                ? `${siteUrl}/book/return?type=package&purchase_id=${checkoutData.purchaseId}&package_id=${checkoutData.packageData?.id || ''}`
                : checkoutData.isClassPayment && checkoutData.participantId
                  ? `${siteUrl}/book/return?type=class&participant_id=${checkoutData.participantId}`
                  : `${siteUrl}/book/return?booking_id=${checkoutData.bookingId}`);

  return (
    <MoyasarCheckoutModal
      contentClassName={presentation.className}
      contentStyle={presentation.style}
      isOpen={isOpen}
      onClose={handleClose}
      paymentConfig={{
        // Moyasar is a Saudi PSP — settles in SAR only. Forced to SAR regardless
        // of tenant's display currency. PSP currency gating belongs at the tenant
        // currency-picker level (`apps/web/app/api/currencies/route.ts`).
        amount: toMinorUnits(bookingDetails.amount, 'SAR'),
        currency: 'SAR',
        description: bookingDetails.description,
        callback_url: callbackUrl,
        publishable_api_key: checkoutData.publishableKey,
        metadata: {
          ...(checkoutData.isPackagePayment && checkoutData.userPackageId
            ? {
                user_package_id: checkoutData.userPackageId,
                type: 'package_payment',
                tenant_id: checkoutData.tenantId || '',
                tenant_name: checkoutData.tenantName || '',
                ...(checkoutData.locationId && {
                  location_id: checkoutData.locationId,
                }),
              }
            : checkoutData.isMembershipPayment && checkoutData.userMembershipId
              ? {
                  user_membership_id: checkoutData.userMembershipId,
                  type: 'membership',
                  tenant_id: checkoutData.tenantId || '',
                  tenant_name: checkoutData.tenantName || '',
                  ...(checkoutData.locationId && {
                    location_id: checkoutData.locationId,
                  }),
                }
              : checkoutData.isMembershipPurchase &&
                  checkoutData.membershipPlanId
                ? {
                    type: 'membership_purchase',
                    plan_id: checkoutData.membershipPlanId,
                    user_id: checkoutData.userId || '',
                    period: checkoutData.membershipPeriod || '',
                    purchase_id: checkoutData.membershipPurchaseId || '',
                    tenant_id: checkoutData.tenantId || '',
                    tenant_name: checkoutData.tenantName || '',
                    ...(checkoutData.locationId && {
                      location_id: checkoutData.locationId,
                    }),
                  }
                : checkoutData.isGuestPassPayment && checkoutData.guestPassId
                  ? {
                      guest_pass_id: checkoutData.guestPassId,
                      type: 'guest_pass',
                      tenant_id: checkoutData.tenantId || '',
                      tenant_name: checkoutData.tenantName || '',
                      ...(checkoutData.locationId && {
                        location_id: checkoutData.locationId,
                      }),
                    }
                  : checkoutData.purchaseId
                    ? {
                        purchase_id: checkoutData.purchaseId,
                        package_id: checkoutData.packageData?.id || '',
                        type: 'package_purchase',
                        user_id: checkoutData.userId || '',
                        player_count: (
                          checkoutData.playerCount ?? 1
                        ).toString(),
                        tenant_id: checkoutData.tenantId || '',
                        tenant_name: checkoutData.tenantName || '',
                        ...(checkoutData.locationId && {
                          location_id: checkoutData.locationId,
                        }),
                      }
                    : checkoutData.isClassPayment && checkoutData.participantId
                      ? {
                          participantId: checkoutData.participantId,
                          booking_id: checkoutData.bookingId || '',
                          type: 'class_registration',
                          isClassPayment: 'true',
                          tenant_id: checkoutData.tenantId || '',
                          tenant_name: checkoutData.tenantName || '',
                          ...(checkoutData.locationId && {
                            location_id: checkoutData.locationId,
                          }),
                          ...(checkoutData.isAddonPayment && {
                            is_addon_payment: 'true',
                          }),
                        }
                      : {
                          booking_id: checkoutData.bookingId || '',
                          type: 'court_booking',
                          tenant_id: checkoutData.tenantId || '',
                          tenant_name: checkoutData.tenantName || '',
                          ...(checkoutData.locationId && {
                            location_id: checkoutData.locationId,
                          }),
                          ...(checkoutData.isAddonPayment && {
                            is_addon_payment: 'true',
                          }),
                        }),
          // extraMetadata goes LAST so callers can override top-level fields
          // (e.g. fixed-slot carries its own keys alongside the package_purchase base).
          ...(checkoutData.extraMetadata || {}),
        },
        supported_networks: ['visa', 'mastercard', 'mada'],
        methods: ['creditcard'],
      }}
      // Hide Apple Pay once a card payment has been submitted (isProcessing
      // covers the window between the card widget's on_completed and the 3DS
      // redirect) — two live payment paths must never overlap, or both could
      // charge for the same purchase.
      applePaySlot={isProcessing ? undefined : applePaySlot}
      onSuccess={handlePaymentSuccess}
      onError={handlePaymentError}
    />
  );
}
